'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/shared/ui/button';
import { Bell } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { usePushNotifications } from '@/lib/features/notifications/use-push-notifications';
import { useAuth } from '@/lib/integrations/supabase';
import * as Sentry from '@sentry/nextjs';

const STORAGE_KEY = 'pwa_notification_prompt_shown';
const AUTO_SUBSCRIBE_KEY = 'pwa_auto_subscribed';

/**
 * Detects if the app is running in PWA/standalone mode
 */
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  
  // iOS Safari standalone mode
  const isIOSStandalone = (window.navigator as any).standalone === true;
  
  // Android/Chrome standalone mode
  const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
  
  // Android app mode
  const isAndroidApp = document.referrer.includes('android-app://');
  
  return isIOSStandalone || isStandaloneDisplay || isAndroidApp;
}

/**
 * Detects if the device is mobile
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for mobile devices
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
}

export function NotificationPermissionPrompt() {
  const [show, setShow] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { isSupported, permission, subscribe } = usePushNotifications();
  const { user } = useAuth();

  // Listen for permission changes when app returns to foreground
  useEffect(() => {
    if (!isProcessing) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if permission was granted while app was in background
        const currentPermission = Notification.permission;
        if (currentPermission === 'granted') {
          // Permission was granted, close the modal
          localStorage.setItem(STORAGE_KEY, 'true');
          setShow(false);
          setIsProcessing(false);
        } else if (currentPermission === 'denied') {
          // Permission was denied, close the modal
          localStorage.setItem(STORAGE_KEY, 'true');
          setShow(false);
          setIsProcessing(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isProcessing]);

  // Poll for permission changes while processing (for iOS Safari quirks)
  useEffect(() => {
    if (!isProcessing) return;

    const pollInterval = setInterval(() => {
      const currentPermission = Notification.permission;
      if (currentPermission !== 'default') {
        // Permission has changed, close the modal
        localStorage.setItem(STORAGE_KEY, 'true');
        setShow(false);
        setIsProcessing(false);
      }
    }, 100); // Poll every 100ms

    return () => clearInterval(pollInterval);
  }, [isProcessing]);

  useEffect(() => {
    // Only show if:
    // 1. User is authenticated
    // 2. Running on mobile device
    // 3. In PWA/standalone mode
    // 4. Push notifications are supported
    // 5. Permission is 'default' (not granted or denied yet)
    // 6. Prompt hasn't been shown before
    // 7. Auto-subscription hasn't already been attempted
    if (typeof window === 'undefined') return;
    if (!user) return;
    if (!isMobileDevice()) return;
    if (!isStandaloneMode()) return;
    if (!isSupported) return;
    // Don't show if permission is already granted or denied
    if (permission !== 'default') return;

    // Check if prompt was already shown
    const hasBeenShown = localStorage.getItem(STORAGE_KEY) === 'true';
    if (hasBeenShown) return;

    // Check if auto-subscription has already been attempted
    // This prompt serves as a fallback for when auto-subscription fails or
    // when a user gesture is required (some browsers require it)
    const hasAutoSubscribed = localStorage.getItem(AUTO_SUBSCRIBE_KEY) === 'true';

    // Longer delay to give auto-subscription time to complete first (runs at 2s)
    // This prompt acts as a fallback with manual user interaction
    const timer = setTimeout(() => {
      // Re-check permission state before showing (auto-sub might have succeeded)
      if (Notification.permission === 'default') {
        setShow(true);
      }
    }, hasAutoSubscribed ? 1500 : 4000); // Shorter delay if auto-sub already tried

    return () => clearTimeout(timer);
  }, [user, isSupported, permission]);

  const handleAllow = async () => {
    setIsProcessing(true);

    try {
      // Subscribe to push notifications (this will also request permission)
      await subscribe();

      // Double-check permission state immediately after subscribe completes
      const finalPermission = Notification.permission;

      // Mark as shown regardless of outcome
      localStorage.setItem(STORAGE_KEY, 'true');

      // Close the prompt only if permission was actually granted or denied
      if (finalPermission !== 'default') {
        setShow(false);
        setIsProcessing(false);
      }
      // Otherwise, polling and visibility listeners will handle it
    } catch (error) {
      Sentry.captureException(error);
      // Still mark as shown to prevent infinite prompts
      localStorage.setItem(STORAGE_KEY, 'true');
      setShow(false);
      setIsProcessing(false);
    }
  };

  const handleDecline = () => {
    // Mark as shown so we don't prompt again
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[100] backdrop-blur-sm"
            onClick={handleDecline}
          />
          
          {/* iOS-like Permission Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 bottom-20 z-[101] pointer-events-none"
          >
            <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden pointer-events-auto max-w-sm mx-auto">
              {/* Icon Header */}
              <div className="flex flex-col items-center pt-8 pb-4 px-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Bell className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2 text-center">
                  Enable Notifications
                </h3>
                <p className="text-sm text-muted-foreground text-center leading-relaxed">
                  Stay updated with appointment reminders, confirmations, and important updates.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-border divide-y divide-border">
                <Button
                  onClick={handleAllow}
                  disabled={isProcessing}
                  className="w-full rounded-none h-14 text-base font-medium bg-transparent hover:bg-muted/50 text-primary"
                  variant="ghost"
                >
                  {isProcessing ? 'Enabling...' : 'Allow'}
                </Button>
                <Button
                  onClick={handleDecline}
                  disabled={isProcessing}
                  className="w-full rounded-none h-14 text-base font-medium bg-transparent hover:bg-muted/50 text-foreground"
                  variant="ghost"
                >
                  Don't Allow
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

